import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { fadeUp, staggerContainer, viewport } from '../../lib/motion';

const chapters = [
  { n: '01', title: 'Clarity over complexity', body: 'We translate tangled financials into clear, confident decisions — so you always know where your business stands.' },
  { n: '02', title: 'Compliance without stress', body: 'From GST to income tax and audits, we keep you fully compliant and ahead of every deadline.' },
  { n: '03', title: 'Growth as a partnership', body: 'Beyond the books, we advise on cash flow, structure and strategy to help you scale profitably.' },
];

const stats = [['500+', 'Clients Served'], ['20+', 'Years Experience'], ['98%', 'Satisfaction']];

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <section id="about" ref={ref} className="relative px-6 lg:px-10 py-28 bg-ink-900">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
        <div className="lg:col-span-6">
          <motion.div variants={staggerContainer()} initial="hidden" whileInView="show" viewport={viewport}>
            <motion.span variants={fadeUp} className="text-xs tracking-[0.2em] uppercase font-semibold text-gold-light">About SN Associates</motion.span>
            <motion.h2 variants={fadeUp} className="font-display text-4xl sm:text-5xl text-white tracking-tight mt-3 mb-10">A finance partner you can trust</motion.h2>
          </motion.div>

          <motion.div variants={staggerContainer(0.15)} initial="hidden" whileInView="show" viewport={viewport} className="space-y-8">
            {chapters.map((c) => (
              <motion.div key={c.n} variants={fadeUp} className="flex gap-6 group">
                <span className="font-mono text-sm text-gold-light pt-1 shrink-0">{c.n}</span>
                <div className="border-l border-slate-800 group-hover:border-gold/50 transition-colors pl-6 pb-2">
                  <h3 className="font-display text-2xl text-white">{c.title}</h3>
                  <p className="text-slate-400 mt-2 leading-relaxed">{c.body}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div variants={staggerContainer(0.1)} initial="hidden" whileInView="show" viewport={viewport} className="grid grid-cols-3 gap-4 mt-12">
            {stats.map(([n, l]) => (
              <motion.div variants={fadeUp} key={l} className="bg-ink-800 border border-slate-800 rounded-2xl p-5 text-center">
                <div className="font-display text-3xl text-gold-light">{n}</div>
                <div className="text-xs text-slate-400 mt-1">{l}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-6 relative">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} className="relative rounded-3xl overflow-hidden border border-white/10 h-[520px]">
            <motion.img
              style={{ y: imgY }}
              src="https://images.pexels.com/photos/28506813/pexels-photo-28506813.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"
              alt="SN Associates office"
              className="w-full h-[115%] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 bg-ink-900/70 backdrop-blur-xl border border-white/10 rounded-2xl p-5">
              <p className="font-display italic text-lg text-white">"We simplify finance so you can focus on what you do best."</p>
              <p className="text-xs text-gold-light mt-2 tracking-wide">— SN Associates, Hyderabad</p>
            </div>
          </motion.div>
          <div className="absolute -z-10 -inset-4 bg-gold/10 blur-3xl rounded-full" />
        </div>
      </div>
    </section>
  );
}
